<template>
    <div v-if="visible" class="cookie-consent" role="dialog" aria-modal="false" :aria-label="$t('cookieBannerTitle')">
        <div class="cookie-consent-body">
            <div class="cookie-consent-header">
                <h3 class="cookie-consent-title"><i class="fa fa-lock" aria-hidden="true"></i>{{ showSettings ? $t("cookieSettings") : $t("cookieBannerTitle") }}</h3>
                <button v-if="canClose" type="button" class="cookie-consent-close" :aria-label="$t('cookieClose')" @click="close">
                    &times;
                </button>
            </div>

            <p v-if="!showSettings" class="cookie-consent-text">{{ $t("cookieBannerText") }}</p>

            <div v-else class="cookie-consent-settings">
                <div class="cookie-category">
                    <div class="cookie-category-header">
                        <span class="cookie-category-title">{{ $t("cookieNecessaryTitle") }}</span>
                        <span class="cookie-always-on">{{ $t("cookieAlwaysOn") }}</span>
                    </div>
                    <p class="cookie-category-text">{{ $t("cookieNecessaryText") }}</p>
                </div>
                <div class="cookie-category">
                    <div class="cookie-category-header">
                        <label class="cookie-category-title mb-0" for="cookie-analytics-switch">{{ $t("cookieAnalyticsTitle") }}</label>
                        <div class="custom-control custom-switch">
                            <input id="cookie-analytics-switch" v-model="analyticsChoice" type="checkbox" class="custom-control-input">
                            <label class="custom-control-label" for="cookie-analytics-switch"><span class="sr-only">{{ $t("cookieAnalyticsTitle") }}</span></label>
                        </div>
                    </div>
                    <p class="cookie-category-text">{{ $t("cookieAnalyticsText") }}</p>
                </div>
            </div>
        </div>

        <div class="cookie-consent-actions">
            <button type="button" class="cookie-btn cookie-btn-primary" @click="acceptAll">{{ $t("cookieAcceptAll") }}</button>
            <template v-if="!showSettings">
                <button type="button" class="cookie-btn cookie-btn-secondary" @click="rejectAnalytics">{{ $t("cookieRejectAnalytics") }}</button>
                <button type="button" class="cookie-btn cookie-btn-secondary cookie-btn-right" @click="openSettings">{{ $t("cookieCustomize") }}</button>
            </template>
            <button v-else type="button" class="cookie-btn cookie-btn-secondary cookie-btn-right" @click="saveSettings">{{ $t("cookieSaveSettings") }}</button>
        </div>

        <div class="cookie-consent-footer">
            {{ $t("cookieLearnMorePrefix") }}
            <router-link :to="policyRoute" @click.native="onPolicyClick">{{ $t("cookieLearnMoreLink") }}</router-link>.
        </div>
    </div>
</template>

<script>
import { consentState, saveConsent, openCookieSettings, closeCookieSettings } from "../../cookieConsent.js"

export default {
    data() {
        return {
            state: consentState,
            analyticsChoice: false
        }
    },
    computed: {
        hasConsent() {
            return this.state.consent !== null;
        },
        visible() {
            return !this.hasConsent || this.state.settingsOpen;
        },
        showSettings() {
            return this.state.settingsOpen;
        },
        canClose() {
            return this.hasConsent && this.state.settingsOpen;
        },
        policyRoute() {
            const lang = this.$route.params.lang || this.$i18n.locale;
            const name = lang === "en" ? "cookie-policy" : "politika-kolacica";
            return { name: name, params: { lang: lang } };
        }
    },
    watch: {
        "state.settingsOpen"(open) {
            if (open) {
                this.analyticsChoice = this.hasConsent ? this.state.consent.analytics : false;
            }
        }
    },
    methods: {
        acceptAll() {
            saveConsent(true);
        },
        rejectAnalytics() {
            saveConsent(false);
        },
        openSettings() {
            openCookieSettings();
        },
        saveSettings() {
            saveConsent(this.analyticsChoice);
        },
        close() {
            closeCookieSettings();
        },
        onPolicyClick() {
            if (this.hasConsent) {
                closeCookieSettings();
            }
        }
    }
}
</script>

<style scoped>
.cookie-consent {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 2000;
    width: 560px;
    max-width: calc(100% - 32px);
    max-height: calc(100vh - 48px);
    overflow-y: auto;
    background-color: #ffffff;
    border: 1px solid var(--secondary-color-hover);
    border-radius: 15px;
    box-shadow: 0 8px 30px rgba(30, 66, 138, 0.18);
    color: #465481;
    font-size: 1.05rem;
}

.cookie-consent-body {
    padding: 20px 24px 4px;
}

.cookie-consent-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
}

.cookie-consent-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.5rem;
    color: var(--main-color);
    margin: 0;
}

.cookie-consent-close {
    background: none;
    border: none;
    font-size: 1.8rem;
    line-height: 1;
    color: var(--main-color);
    cursor: pointer;
    padding: 0 4px;
}

.cookie-consent-close:hover {
    color: var(--main-color-active);
}

.cookie-consent-text {
    line-height: 1.45;
    margin: 0 0 16px;
}

.cookie-category {
    background-color: var(--light-color);
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 12px;
}

.cookie-category-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.cookie-category-title {
    font-weight: 600;
    color: var(--main-color);
}

.cookie-always-on {
    font-size: 0.9rem;
    color: #465481;
    white-space: nowrap;
}

.cookie-category-text {
    font-size: 0.95rem;
    line-height: 1.4;
    margin: 6px 0 0;
}

.cookie-consent .custom-control-input:checked ~ .custom-control-label::before {
    background-color: var(--main-color);
    border-color: var(--main-color);
}

.cookie-consent-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 24px 18px;
}

.cookie-btn {
    border: 1px solid var(--main-color);
    border-radius: .5rem;
    padding: 6px 18px;
    font-size: 1.05rem;
    cursor: pointer;
    transition: color 0.3s ease, background-color 0.3s ease;
}

.cookie-btn-primary {
    background-color: var(--main-color);
    color: #ffffff;
}

.cookie-btn-primary:hover {
    background-color: var(--main-color-active);
    border-color: var(--main-color-active);
}

.cookie-btn-secondary {
    background-color: transparent;
    color: var(--main-color);
}

.cookie-btn-secondary:hover {
    background-color: var(--main-color);
    color: #ffffff;
}

.cookie-btn-right {
    margin-left: auto;
}

.cookie-consent-footer {
    background-color: var(--secondary-color);
    padding: 8px 24px;
    font-size: 0.95rem;
}

.cookie-consent-footer a {
    color: var(--main-color);
    font-weight: bold;
}

@media only screen and (max-width: 576px) {
    .cookie-consent {
        left: 16px;
        right: 16px;
        bottom: 16px;
        width: auto;
        max-width: none;
    }

    .cookie-consent-body {
        padding: 16px 16px 4px;
    }

    .cookie-consent-actions {
        flex-direction: column;
        padding: 8px 16px 16px;
    }

    .cookie-btn,
    .cookie-btn-right {
        width: 100%;
        margin-left: 0;
    }

    .cookie-consent-footer {
        padding: 8px 16px;
    }
}
</style>
